import { useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";

interface UseSessionOptions {
  checkOnFocus?: boolean;
}

const useSession = (options: UseSessionOptions = {}) => {
  const { checkOnFocus = true } = options;

  const {
    validateSession,
    isLoggedIn,
    isGuest,
    sessionExpired,
    clearSessionExpired,
  } = useAuth();

  // 1. Visibility change — revalidate when tab comes back into focus
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible" && checkOnFocus && isLoggedIn) {
      validateSession();
    }
  }, [checkOnFocus, isLoggedIn, validateSession]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [handleVisibilityChange]);

  // ✅ Fixed: was stale closure with empty deps, isLoggedIn was always null
  // Now reacts properly to null → true transition after init
  useEffect(() => {
    if (isLoggedIn === true) {
      validateSession();
    }
  }, [isLoggedIn]);

  return {
    isLoggedIn,
    sessionExpired,
    validateSession,
    isGuest,
    clearSessionExpired, // ✅ exposed so MainLayout can clear stale flag
  };
};

export default useSession;